import { Component, OnInit, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { LoadingController, ModalController } from '@ionic/angular';
import { BadgeContent, ApiV1Job, PositionDetailResponse, RequirementCard, JobFilterType } from '@bluecrew/crew-member-backend-angular-client';
import { InfoModalComponent } from '../common/info-modal/info-modal.component';
import { JobType, jobTypeToIcon } from '../models/job';
import { SegmentCMOSService } from '../services/segment-cmos.service';
import { UserService } from '../services/user.service';
import { presentLoader } from '../util/ion-util';
import { UrlHistoryService } from '../services/url-history.service';
import { PositionService } from '../services/position.service';
import { JobUnavailableModalComponent } from './job-unavailable-modal/job-unavailable-modal.component';
import { AppRoutingUtil } from '../util/app-routing-util';
import { skip } from 'rxjs/operators';
import { filterJobs } from '../util/job-util';
import { InvalidJobModalComponent } from '../application-home/invalid-job-modal/invalid-job-modal.component';
import { PixelEventType, AppcastService } from '../services/appcast.service';
import { JobService } from '../services/job.service';
import _ from 'lodash';
import { SplitService } from '../services/split.service';
import { COMMUTE_PLANNING_SPLIT } from '../util/split-util';

@Component({
  selector: 'app-position',
  templateUrl: './position.page.html',
  styleUrls: ['./position.page.scss'],
})
export class PositionPage implements OnInit {

  private map: google.maps.Map;
  private viewEventEmitted: { [jobId: string]: true } = {};
  private isCommutePlanningSplitOn: boolean;

  pageName = PositionPage.name;
  backNavPath: string;
  jobId: string;
  showSingleSchedule: Boolean = false;
  queryParamMap: ParamMap;
  positionDetail: PositionDetailResponse;
  positionId: string;
  positionJobs: ApiV1Job[];
  blueshifts: ApiV1Job[];
  blueshiftsExcludingOverlaps: ApiV1Job[];
  isBlueshiftCardSelected: Boolean = false;
  selectedJob: ApiV1Job;
  currentJobIndex: number;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef,
              private readonly loadingCtrl: LoadingController,
              private readonly location: Location,
              private readonly modalCtrl: ModalController,
              private readonly positionService: PositionService,
              private readonly jobService: JobService,
              private readonly route: ActivatedRoute,
              private readonly segmentCMOS: SegmentCMOSService,
              private readonly urlHistory: UrlHistoryService,
              private readonly userService: UserService,
              private readonly appcastService: AppcastService,
              private readonly splitService: SplitService
              ) {

    /**
     * Subscribe on changes to the queryParamMap in order to capture when deeplinks change
     * The skip is needed because Router Observables are BehaviorSubjects which notify on initial value,
     * meaning loadData() was getting called twice https://stackoverflow.com/questions/39861547/angular2-query-params-subscription-fires-twice
     */
    this.route.queryParamMap.pipe(skip(1)).subscribe(async (newQueryParamMap) => {
      this.queryParamMap = newQueryParamMap;
      await this.loadData();
    });
  }

  async ngOnInit() {
    this.jobId = this.route.snapshot.paramMap.get('jobId');
    this.positionId = this.route.snapshot.paramMap.get('positionId');
    this.queryParamMap = this.route.snapshot.queryParamMap;

    // If we deeplink through the /job/jobId route, change the route to /position/job/jobId
    if (this.route['_routerState'].snapshot.url.startsWith(AppRoutingUtil.JOB_ROUTE_PREFIX)) {
      this.location.replaceState(AppRoutingUtil.getPositionJobRoute(this.jobId), '',  null);
    }

    await this.loadData();
  }

  private async loadData(onReload: boolean = false) {
    const loader = await presentLoader(this.loadingCtrl);
    this.isCommutePlanningSplitOn = await this.splitService.isFeatureEnabled(COMMUTE_PLANNING_SPLIT);
    let segmentProps = {}
    try {
      segmentProps = { jobId: this.jobId, positionId: this.positionId };
      if (this.positionId) {
        const positionDetailPromise = this.isUserAuthenticated()
          ? this.positionService.getPosition(this.positionId).toPromise()
          : this.positionService.getPositionAnonymous(this.positionId).toPromise();
        this.positionDetail = await positionDetailPromise;
        this.jobId = this.positionDetail?.position?.directToExternalJobId;
        segmentProps = { jobId: this.jobId, positionId: this.positionId };
      } else if (this.jobId) {
        this.showSingleSchedule = this.queryParamMap.get('showSingleSchedule') === 'true'; // show only this job's schedule for the position

        /* When the showSingleSchedule Query Param is true, we want to fetch a only single job from the database.
           This will support the deep link use case: https://bluecrewjobs.atlassian.net/browse/CMP-148 */
        if (this.showSingleSchedule) {
          const jobDetailPromise = this.isUserAuthenticated()
            ? this.jobService.getJobDetail(this.jobId).toPromise()
            : this.jobService.getJobDetailAnonymous(this.jobId).toPromise();

          let jobDetail = await jobDetailPromise;

          this.positionDetail = {
            position: {
              positionId: jobDetail.job.positionId,
              jobs: Array.of(jobDetail.job),
              wageRange: {
                min: jobDetail.job.wage,
                max: jobDetail.job.wage
              },
              aggregatedBadges: _.cloneDeep(jobDetail.job.badges),
              directToExternalJobId: jobDetail.job.legacyExternalJobId,
              requirementCards: _.cloneDeep(jobDetail.job.requirementCards)
            },
            reviews: jobDetail.reviews
          }
        } else /* Normal flow. Shows a collection of jobs under the same position */ {
          const positionDetailPromise = this.isUserAuthenticated()
            ? this.positionService.getPositionFromJobId(this.jobId).toPromise()
            : this.positionService.getPositionFromJobIdAnonymous(this.jobId).toPromise();

          this.positionDetail = await positionDetailPromise;
        }

        this.jobId = this.positionDetail?.position?.directToExternalJobId;
        segmentProps = { jobId: this.jobId, positionId: this.positionDetail?.position?.positionId, singleScheduleParam: this.showSingleSchedule};
      } else {
        // we should never reach this.
        this.segmentCMOS.addEvent('positionDetail_loadFailure', { reason: "positionId and jobId do not exist" });
        this.goBack();
      }
      const jobs = this.positionDetail.position.jobs;
      this.aggregateBlueshiftsAndSetPositionJobs(jobs);
      if (onReload) {
        if (this.positionJobs.length > 0 || this.hasBlueshifts()) {
          this.showSingleSchedule = false;
          let lastSlideIndex: number;
          if (this.hasBlueshifts() && this.positionJobs.length > 0) {
            lastSlideIndex = this.positionJobs.length;
          } else if (this.positionJobs.length > 0) {
            lastSlideIndex = this.positionJobs.length - 1;
          } else { // show only Blueshift slide
            lastSlideIndex = 0;
          };
          this.setJobSelectedIndex(this.currentJobIndex >= lastSlideIndex ? lastSlideIndex : this.currentJobIndex);
        } else {
          this.goBack();
        }
      } else {
        this.currentJobIndex = this.findAnIndexInPosition(this.jobId);
        let currentJob: ApiV1Job;
        if (this.jobId && this.jobId !== 'blueshift' && this.currentJobIndex >= 0) {
          currentJob = this.getJobForCardIndex(this.currentJobIndex);
        }
        if (currentJob && currentJob.primaryFilterReason == JobFilterType.ScheduleConflict) {
          this.segmentCMOS.addEvent('positionDetail_scheduleConflict', { jobId: this.jobId, positionId: this.positionDetail.position.positionId});
          this.positionJobs = filterJobs(this.positionJobs)
          if (this.positionJobs.length == 0) {
            this.openJobScheduleConflictModal(false, currentJob);
          } else {
            this.openJobScheduleConflictModal(true, currentJob);
            this.setJobSelectedIndex(0);
          }
        } else {
          this.positionJobs = filterJobs(this.positionJobs);
          this.currentJobIndex = this.findAnIndexInPosition(this.jobId);
          if (this.currentJobIndex < 0) {  // Job got filtered out
            this.segmentCMOS.addEvent('positionDetail_jobUnavailable', { reason: `job ${this.jobId} is not available in this position ${this.positionDetail.position.positionId}`});
            if (this.positionJobs.length == 0) {
              this.openJobUnavailableModal(false, currentJob);
              this.goBack();
            } else {
              this.openJobUnavailableModal(true, currentJob);
              this.setJobSelectedIndex(0);
            }
          }
        }
        this.selectedJob = this.positionId ? (this.hasBlueshifts() ? this.getSelectedJobForBlueshifts() : this.getJobForCardIndex(0)) : this.getJobForCardIndex(this.findAnIndexInPosition(this.jobId));
      }
      if (this.map) {
        this.setMapData();
      }
      this.isBlueshiftCardSelected = this.hasBlueshifts() && this.currentJobIndex === 0;
      this.segmentCMOS.addEvent('positionDetail_loaded', segmentProps);
      this.segmentCMOS.addEvent(`viewPositionDetails`, { ...segmentProps, pageLoad: 'true' });
      this.emitJobView();
      this.appcastService.fireTrackingPixel(PixelEventType.POSITION_DETAIL_PAGE_VIEW, this.userService.currentUser?.userInfo?.externalId, this.jobId);
    } catch (err) {
      this.segmentCMOS.addEvent('positionDetail_loadFailure', { ...segmentProps, reason: err?.message });
      if (this.positionJobs == null || this.positionJobs.length == 0) {
        this.openJobUnavailableModal(false);
      }
      this.goBack();
    } finally {
      await loader.dismiss();
    }
  }

  private setMapData() {
    const center = { lat: this.selectedJob.addressDetails.latitude, lng: this.selectedJob.addressDetails.longitude };
    this.map.setOptions({
      center,
      clickableIcons: false,
      disableDefaultUI: true,
      fullscreenControl: false,
      gestureHandling: 'none',
      keyboardShortcuts: false,
      zoom: 13.5,
      zoomControl: false,
    });
    var icon = {
      url: '/assets/icons/map-pin.svg',
      scaledSize: new google.maps.Size(50, 50),
    };
    new google.maps.Marker({
      position: center,
      map: this.map,
      icon: icon,
    });
  }

  private findAnIndexInPosition(jobIdToSearch: string): number {
    const targetIndex = this.positionJobs.findIndex(job => job.legacyExternalJobId === jobIdToSearch);
    if (targetIndex === -1 && jobIdToSearch !== 'blueshift') {
      return -1;
    } else if (this.hasBlueshifts()) {
      return targetIndex + 1;
    } else {
      return targetIndex;
    }
  }

  async reloadData() {
    await this.loadData(true);
  }

  onMapReady(map: google.maps.Map) {
    this.map = map;
    if (this.selectedJob) {
      this.setMapData();
    }
  }

  onMapError(error: any) {
    console.error(error);
  }

  onPrevious() {
    this.segmentCMOS.addEvent('positionDetail_navigateBack', { jobId: this.jobId, positionId: this.selectedJob?.positionId });
    this.goBack();
  }

  shouldShowSingleSchedule() {
    if (this.hasBlueshifts()) {
      return this.positionJobs.length === 0 || this.showSingleSchedule;
    } else {
      return this.positionJobs.length == 1 || this.showSingleSchedule;
    }
  }

  goBack() {
    this.urlHistory.goBackWithDefault(this.isUserAuthenticated() ? AppRoutingUtil.BROWSE_JOBS : '/');
  }

  /**
   * Error event-handler for the workplace <img> thumbnail.
   */
  fallbackWorkplaceImg = ($event: ErrorEvent) => {
    ($event.target as HTMLImageElement).src = '/assets/icons/building.svg';
  };

  getPositionIcon() {
    return jobTypeToIcon[this.selectedJob?.jobType as JobType] || '/assets/icons/building.svg';
  }

  isUserAuthenticated() {
    return !!this.userService.currentUser;
  }

  getDistanceInMiles(distanceInMeters: number) {
    const distance = distanceInMeters * 0.000621371192;
    return distance.toFixed(1);
  }

  async openApplicationTypeInfoModal(badge: BadgeContent) {
    const m = await this.modalCtrl.create({
      component: InfoModalComponent,
      cssClass: 'flex-center-modal',
      componentProps: {
        headerText: badge?.text?.text,
        headerIcon: badge?.icon,
        bodyText: [{
          text: badge?.description,
        }],
        dismissButtonText: 'Got it',
      },
    });
    await m.present();
  }

  async openJobUnavailableModal(hasOtherSchedules: Boolean, job?: ApiV1Job) {
    const eventEmitter = new EventEmitter();
    const m = await this.modalCtrl.create({
      component: JobUnavailableModalComponent,
      cssClass: 'bottom-sheet-modal',
      componentProps: {
        confirmEvent: eventEmitter,
        hasOtherSchedules: hasOtherSchedules,
        isJobDirectInvite: job && job.type == 'DIRECTINVITE',
        jobExternalId: job?.legacyExternalJobId
      }
    });
    if (!hasOtherSchedules) {
      eventEmitter.subscribe(() => {
        this.directUserBack();
      });
    }
    await m.present();
  }

  async openJobScheduleConflictModal(hasOtherSchedules: Boolean, job: ApiV1Job) {
    const m = await this.modalCtrl.create({
      component: InvalidJobModalComponent,
      cssClass: hasOtherSchedules ? 'center-modal' : 'fullscreen-modal',
      componentProps: {
        headerText: 'This job conflicts with your schedule',
        headerIcon: '/assets/icons/job-schedule-conflict.svg',
        bodyText: hasOtherSchedules ?
          [{text: 'This job conflicts with another one of your Bluecrew assignments, but there are other schedule options available at this position.'}] :
          [{text: 'Unfortunately, this job conflicts with another one of your Bluecrew assignments. \n \n Check out Bluecrew’s many other jobs to find one that fits your schedule.'}],
        buttonText: 'Find work',
        shouldNavigateBrowseJobs: !hasOtherSchedules,
        isJobDirectInvite: job.type == 'DIRECTINVITE',
        jobExternalId: job.legacyExternalJobId
      }
    });
    if (!hasOtherSchedules) this.goBack();
    await m.present();
  }

  emitJobView() {
    if (this.jobId !== 'blueshift' && !this.viewEventEmitted[this.jobId]) {
      this.viewEventEmitted[this.jobId] = true;
      this.segmentCMOS.addEvent(`viewJobDetails`, { jobId: this.jobId, pageLoad: 'true' });
    }
  }

  setJobSelectedIndex(jobIndex: number) {
    if (!this.blueshifts || this.blueshifts.length === 0) { // no blueshifts
      this.jobId = this.positionJobs[jobIndex].legacyExternalJobId;
      this.emitJobView();
      this.selectedJob = this.positionJobs[jobIndex];
      this.currentJobIndex = jobIndex;
      this.changeDetectorRef.detectChanges();
      this.location.replaceState(AppRoutingUtil.getPositionJobRoute(this.jobId), '',  null);
    } else if (this.blueshifts.length > 0 && jobIndex > 0) { // blueshift card not selected
      this.isBlueshiftCardSelected = false;
      this.jobId = this.positionJobs[jobIndex - 1].legacyExternalJobId;
      this.emitJobView();
      this.selectedJob = this.positionJobs[jobIndex - 1];
      this.currentJobIndex = jobIndex - 1;
      this.changeDetectorRef.detectChanges();
      this.location.replaceState(AppRoutingUtil.getPositionJobRoute(this.jobId), '',  null);
    } else { // blueshift card selected
      this.isBlueshiftCardSelected = true;
      this.jobId = 'blueshift';
      this.selectedJob = this.getSelectedJobForBlueshifts();
      this.currentJobIndex = 0;
      this.changeDetectorRef.detectChanges();
      this.location.replaceState(AppRoutingUtil.getPositionMultiSelectRoute(this.blueshifts[0].positionId.toString()), '',  null);
    }
  }

  async directUserBack() {
    if (this.positionJobs == null || this.positionJobs.length == 0 || filterJobs(this.positionJobs).length == 0) {
      this.goBack();
    } else {
      this.urlHistory.goBackWithDefault(AppRoutingUtil.getPositionRoute(this.positionDetail.position.positionId.toString()));
    }
  }

  private getJobForCardIndex(cardIndex: number): ApiV1Job {
    if (this.hasBlueshifts() && cardIndex === 0) {
      return this.getSelectedJobForBlueshifts();
    } else {
      const indexOffset = this.hasBlueshifts() ? 1 : 0;
      return this.positionJobs[cardIndex - indexOffset];
    }
  }

  private aggregateBlueshiftsAndSetPositionJobs(jobs: ApiV1Job[]) {
    this.positionJobs = [];
    this.blueshifts = [];
    // add to respective list
    jobs.forEach(job => {
      if (job.blueshiftRequestId !== undefined && job.blueshiftRequestId !== null) {
        this.blueshifts.push(job);
      } else {
        this.positionJobs.push(job);
      }
    });
    this.blueshifts = filterJobs(this.blueshifts, [JobFilterType.ScheduleConflict]);
    this.blueshiftsExcludingOverlaps = filterJobs(this.blueshifts);
    if (this.blueshiftsExcludingOverlaps.length === 0) this.blueshifts = [];
  }

  private hasBlueshifts() {
    return this.blueshifts && this.blueshifts.length > 0;
  }

  private getSelectedJobForBlueshifts(): ApiV1Job {
    const badges = this.blueshiftsExcludingOverlaps.reduce((accumulator: BadgeContent[], job: ApiV1Job) => {
      job.badges.forEach(badge => {
        const hasItem = accumulator.find((elem) => elem.badgeType === badge.badgeType);
        if (!hasItem) {
          accumulator.push(badge);
        }
      });
      return accumulator;
    }, []);

    // sort Direct Invite badges first
    badges.sort((a, b) => a.text.text.toLowerCase() === 'direct invite' ? -1 : 0);

    const requirements = this.blueshiftsExcludingOverlaps.reduce((accumulator: string[], job: ApiV1Job) => {
      job.requirements.forEach(requirement => {
        const hasItem = accumulator.find((elem) => elem === requirement);
        if (!hasItem) {
          accumulator.push(requirement);
        }
      });
      return accumulator;
    }, []);

    const requirementCards = this.blueshiftsExcludingOverlaps.reduce((accumulator: RequirementCard[], job: ApiV1Job) => {
      job.requirementCards.forEach(requirementCard => {
        const hasItem = accumulator.find((elem) => elem.title === requirementCard.title);
        if (!hasItem) {
          accumulator.push(requirementCard);
        }
      });
      return accumulator;
    }, []);

    return {
      addressDetails: this.blueshifts[0].addressDetails,
      badges: badges,
      companyName: this.blueshifts[0].companyName,
      description: this.blueshifts[0].description,
      distanceInMeters: this.blueshifts[0].distanceInMeters,
      jobType: this.blueshifts[0].jobType,
      requirements: requirements,
      requirementCards: requirementCards,
      smallLogo: this.blueshifts[0].smallLogo,
      title: this.blueshifts[0].title,
    };
  }
}
